import type { User } from 'oidc-client-ts';
import { message } from 'ant-design-vue';
import router from '@/router';
import store from '@/store';
import { AppContext } from '@/models/AppContext';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import {
  isCognitoLocalEmulator,
  redirectToCommissioningLoginFresh,
} from '@/shared/auth/cognitoLocalEmulator';
import { unpackOidcUser } from '@/shared/auth/cognitoUserAttributes';
import { getReturnUrlFromOidcUser } from '@/shared/auth/postLoginRedirect';
import { useAuthStore } from '@/shared/stores/authStore';
import { authenticateNative } from './nativeCognito';
import { requiresFreshNativeSignIn } from './nativeAuthFeedback';

interface CognitoSignInResult {
  success: boolean;
  returnUrl: string | null;
}

/**
 * Initializes Cognito on app load.
 * Hydrates session and wires OIDC event handlers.
 */
export async function initCognito(): Promise<void> {
  try {
    const authStore = useAuthStore();
    const version = isNativeCognitoEnabled ? cognitoClient.sessionVersion() : 0;
    const user = await __resolveActiveOidcUser();
    if (isNativeCognitoEnabled && version !== cognitoClient.sessionVersion()) return;
    if (user) {
      await authStore.setAuthUser(unpackOidcUser(user));
      if (
        isNativeCognitoEnabled &&
        requiresFreshNativeSignIn(authStore.lifecycleStatus.onboardingState)
      ) {
        await abandonNativeSignIn(version);
      }
    } else if (authStore.token || authStore.cognitoProfile) {
      // Persisted Pinia session without a matching OIDC user
      // (e.g. after switching cognito-local <--> staging).
      // Without this the store would think we are still logged in.
      authStore.$reset();
    }
    __setupUserManagerEvents();
  } catch (error) {
    console.error('Cognito auth init failed:', error);
    useAuthStore().setCognitoInitFailed(true);
  }
}

/**
 * Completes Cognito sign-in from callback.
 * returnUrl comes from OIDC state passed to signinRedirect({ state: { returnUrl } }).
 */
export async function completeCognitoSignInFromCallback(url: string): Promise<CognitoSignInResult> {
  const authStore = useAuthStore();
  let version = 0;
  if (isNativeCognitoEnabled) {
    store.commit('resetStore');
    authStore.$reset();
    const clearing = cognitoClient.removeUser();
    version = cognitoClient.sessionVersion();
    await clearing;
    if (version !== cognitoClient.sessionVersion()) return { success: false, returnUrl: null };
  }
  const cognitoUser = await cognitoClient.signinCallback(url);
  if (isNativeCognitoEnabled && version !== cognitoClient.sessionVersion()) {
    return { success: false, returnUrl: null };
  }
  if (!cognitoUser) {
    return { success: false, returnUrl: null };
  }

  const returnUrl = getReturnUrlFromOidcUser(cognitoUser);
  const success = await authStore.setAuthUser(unpackOidcUser(cognitoUser));
  return { success, returnUrl };
}

/** Native credentials never create an independent Pinia/SDK session. */
export async function completeNativeSignIn(
  email: string,
  password: string,
  returnUrl?: string,
): Promise<boolean> {
  if (!isNativeCognitoEnabled) throw new Error('Native authentication is disabled');
  store.commit('resetStore');
  useAuthStore().$reset();
  const clearing = cognitoClient.removeUser();
  const version = cognitoClient.sessionVersion();
  await clearing;
  const tokens = await authenticateNative(email, password);
  if (version !== cognitoClient.sessionVersion()) return false;
  const user = await cognitoClient.importNativeSession(tokens, version, returnUrl);
  if (!user || version !== cognitoClient.sessionVersion()) return false;
  return useAuthStore().setAuthUser(unpackOidcUser(user));
}

/**
 * Force refresh the current user session by signing in silently.
 * DISREGARD existing user session.
 *
 * Then, update the auth store with the new user session.
 */
export async function refreshCognitoSession(): Promise<void> {
  if (isNativeCognitoEnabled) {
    const version = cognitoClient.sessionVersion();
    try {
      const user = await cognitoClient.signinSilent();
      if (user && version === cognitoClient.sessionVersion()) {
        await useAuthStore().setAuthUser(unpackOidcUser(user));
        if (requiresFreshNativeSignIn(useAuthStore().lifecycleStatus.onboardingState)) {
          if (await abandonNativeSignIn(version)) await router.push('/login');
          throw new Error('Please sign in again to finish setting up your account.');
        }
      }
    } catch (error) {
      if (version === cognitoClient.sessionVersion()) await __clearCognitoSession();
      throw error;
    }
    return;
  }
  try {
    await cognitoClient.signinSilent();
  } catch {
    /* token refresh may fail if session invalid; still attempt getUser */
  }

  const authStore = useAuthStore();
  const user = await cognitoClient.getUser();
  if (user && !user.expired) {
    await authStore.setAuthUser(unpackOidcUser(user));
  }
}

/**
 * Whether the user is logged in for the given app context.
 * Cognito: checks UserManager (with silent renew). Legacy: auth store.
 */
export async function isLoggedIn(appContext: AppContext | null = null): Promise<boolean> {
  if (isCognitoEnabled) {
    const user = await __resolveActiveOidcUser();
    return user != null;
  } else {
    if (appContext === null) {
      throw new Error('App context is required for legacy authentication');
    }
    return useAuthStore().isLegacyLoggedIn(appContext);
  }
}

/**
 * Return the auth token for main-server API calls.
 * - Cognito: Resolves UserManager (with silent renew) and syncs only the OIDC fields to the store.
 * - Legacy: Reads token from auth store.
 */
export async function getAuthToken(appContext: AppContext | null = null): Promise<string | null> {
  if (isCognitoEnabled) {
    const version = isNativeCognitoEnabled ? cognitoClient.sessionVersion() : 0;
    const user = await __resolveActiveOidcUser();
    if (!user || (isNativeCognitoEnabled && version !== cognitoClient.sessionVersion())) {
      return null;
    }

    useAuthStore().setOidcSession(unpackOidcUser(user));
    return user.access_token || null;
  } else {
    if (appContext === null) {
      throw new Error('App context is required for legacy authentication token');
    }
    return useAuthStore().legacyApiToken(appContext);
  }
}

/** Clears the stored legacy SolCentre session (the ComApp session is kept). */
export function endLegacySolcentreSession(): void {
  useAuthStore().resetLegacySolcentreLoginUser();
  store.commit('resetStore');
  localStorage.removeItem('redirectPath');
}

/** Local mutable state to prevent multiple logout operations from running concurrently. */
let __logoutInProgress = false;

/**
 * Ends the app session and navigates to the appropriate login screen.
 * Used for explicit sign-out and HTTP 401 handling.
 */
export async function logout(appContext: AppContext | null = null): Promise<void> {
  if (__logoutInProgress) {
    return;
  }
  __logoutInProgress = true;

  try {
    const authStore = useAuthStore();
    if (isCognitoEnabled) {
      authStore.$reset();
      if (isNativeCognitoEnabled) {
        store.commit('resetStore');
        const clearing = cognitoClient.removeUser();
        const version = cognitoClient.sessionVersion();
        try {
          await clearing;
        } finally {
          if (version === cognitoClient.sessionVersion()) {
            // Hosted cookies can survive native sign-in, reloads and account changes.
            if (isCognitoLocalEmulator()) await router.push('/login');
            else {
              try {
                await cognitoClient.signoutRedirect();
              } catch {
                if (version === cognitoClient.sessionVersion()) {
                  await router.push('/login');
                  if (version === cognitoClient.sessionVersion()) {
                    message.error(
                      'We could not finish signing you out. Please try again before sharing this browser.',
                    );
                  }
                }
              }
            }
          }
        }
        return;
      }
      if (isCognitoLocalEmulator()) {
        await cognitoClient.removeUser();
        redirectToCommissioningLoginFresh();
        return;
      }
      await cognitoClient.signoutRedirect();
      return;
    } else {
      if (appContext === null) {
        throw new Error('App context is required for legacy authentication logout');
      }
      if (appContext === AppContext.ComApp) {
        authStore.clearMainServerComLoginAuth();
        await router.push({ name: 'ComLogin' });
        return;
      } else {
        await router.push('/login');
        endLegacySolcentreSession();
      }
    }
  } finally {
    __logoutInProgress = false;
  }
}

/**
 * Look up the current user session or sign in silently if the user has expired.
 * @returns The current active user or null if unable to renew user session.
 */
async function __resolveActiveOidcUser(): Promise<User | null> {
  const version = isNativeCognitoEnabled ? cognitoClient.sessionVersion() : 0;
  const user = await cognitoClient.getUser();
  if (isNativeCognitoEnabled && version !== cognitoClient.sessionVersion()) return null;

  if (!user) {
    return null;
  }
  if (!user.expired) {
    return user;
  }

  try {
    const refreshed = await cognitoClient.signinSilent();
    return isNativeCognitoEnabled && version !== cognitoClient.sessionVersion() ? null : refreshed;
  } catch {
    if (isNativeCognitoEnabled && version !== cognitoClient.sessionVersion()) return null;
    await __clearCognitoSession();
    return null;
  }
}

async function __clearCognitoSession(): Promise<void> {
  if (isNativeCognitoEnabled) store.commit('resetStore');
  useAuthStore().$reset();
  await cognitoClient.removeUser();
}

/** Clear only the sign-in that owns this completion, without navigating. */
export async function abandonNativeSignIn(expectedVersion: number): Promise<boolean> {
  if (!isNativeCognitoEnabled || expectedVersion !== cognitoClient.sessionVersion()) return false;
  const clearing = __clearCognitoSession();
  const version = cognitoClient.sessionVersion();
  await clearing;
  return version === cognitoClient.sessionVersion();
}

function __setupUserManagerEvents() {
  const authStore = useAuthStore();
  cognitoClient.setupUserManagerEvents({
    onUserLoaded: (loadedUser) => {
      void authStore.setAuthUser(unpackOidcUser(loadedUser));
    },
    onUserUnloaded: () => {
      authStore.$reset();
    },
    onSilentRenewError: () => {
      void logout();
    },
    onAccessTokenExpired: () => {
      if (isNativeCognitoEnabled) {
        const version = cognitoClient.sessionVersion();
        void cognitoClient
          .signinSilent()
          .then((user) => {
            if (user && version === cognitoClient.sessionVersion())
              authStore.setOidcSession(unpackOidcUser(user));
          })
          .catch(() => {
            if (version === cognitoClient.sessionVersion()) void logout();
          });
        return;
      }
      void cognitoClient
        .signinSilent()
        .then(() => undefined)
        .catch(() => logout());
    },
  });
}
